import {
  ArcMapView,
  ArcUI,
  ArcScaleBar,
  ArcZoom,
  useViewState,
  useWatchState,
} from "arcgis-react";
import "./ESRI.css";
import { Layers } from "./Layers";
import Search from "@arcgis/core/widgets/Search";
import {
  initialCenterWCC,
  addressBaseSearchSource,
  handleMobileGeolocationEvent,
  updateMapGivenLatLong,
  isCoordinatesInsideBorough,
} from "./mapUtils";
import { useEffect, useState } from "react";
import { useWasteStore } from "@store/store";
import Widget from "@arcgis/core/widgets/Widget";
import { emptyLocation } from "@store/storeSlices/wasteDetails";
import * as Sentry from "@sentry/react";

export type MapProps = [longitude: number, latitude: number] | null;

export const EsriMap = () => {
  const [inBoundary, setInBoundary] = useState<boolean>(false);
  const [mapView, setMapView] = useViewState<__esri.MapView>();
  const [searchWidget, setSearchWidget] = useState<Search>();
  const { updateLocationOfWaste, locationOfWaste } = useWasteStore();
  const { latitude, longitude } = locationOfWaste;

  const inBoundaryCheck = async () => {
    if (latitude !== null && longitude !== null) {
      const insideBorough = await isCoordinatesInsideBorough([
        Number.parseFloat(longitude),
        Number.parseFloat(latitude),
      ]);
      if (insideBorough) {
        setInBoundary(true);
      }
    }
  };

  useEffect(() => {
    if (latitude !== null && longitude !== null) {
      inBoundaryCheck().catch((err) => {
        Sentry.captureException(err);
      });
    }

    if (mapView !== undefined && searchWidget === undefined) {
      setSearchWidget(
        new Search({
          view: mapView,
          sources: [addressBaseSearchSource(mapView, updateLocationOfWaste)],
          includeDefaultSources: false,
          popupEnabled: false,
        }),
      );
    }

    if (searchWidget !== undefined && mapView !== undefined) {
      searchWidget.on("suggest-start", () => mapView.closePopup());
      searchWidget.on("suggest-start", () => mapView.closePopup());
      searchWidget.on("search-focus", () => {
        mapView.closePopup();
        handleMobileGeolocationEvent(mapView, updateLocationOfWaste);
      });

      searchWidget.on("search-blur", () => mapView.closePopup());
      searchWidget.on("search-clear", () => {
        updateLocationOfWaste(emptyLocation);
        mapView.graphics.removeAll();
      });

      mapView.ui.components = ["attribution"];
      mapView.ui.add(searchWidget, "top-right");

      if (inBoundary) {
        mapView.zoom = 17;
      }

      // Get the input container element
      const searchWidgetEl: Widget | HTMLElement =
        mapView.ui.getComponents("top-right")[0];
      if (searchWidgetEl instanceof Widget) {
        (searchWidgetEl.container as HTMLElement).classList.remove(
          "esri-search",
        );
        (searchWidgetEl.container as HTMLElement).classList.add(
          "w-full",
          "min-[280px]:w-[200px]",
          "min-[320px]:w-[240px]",
          "min-[350px]:w-[280px]",
          "min-[375px]:w-[290px]",
          "min-[390px]:w-[310px]",
          "min-[410px]:w-[330px]",
          "min-[420px]:w-[350px]",
          "md:w-[500px]",
          "min-[820px]:w-[600px]",
          "2xl:w-[650px]",
        );
      }
    }
  }, [mapView, inBoundary, searchWidget]);

  useEffect(() => {
    if (mapView !== undefined) {
      if (latitude && longitude) {
        try {
          void updateMapGivenLatLong(
            Number.parseFloat(latitude),
            Number.parseFloat(longitude),
            mapView,
            updateLocationOfWaste,
          );
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    }
  }, [mapView]);

  const popupVisible = useWatchState(() => mapView?.popup.visible, [mapView]);
  useEffect(() => {
    if (popupVisible && searchWidget !== undefined && locationOfWaste.UPRN) {
      searchWidget.searchTerm = locationOfWaste.officialAddress;
    }
    if (
      popupVisible &&
      locationOfWaste.UPRN === "" &&
      searchWidget !== undefined
    ) {
      searchWidget.clear();
    }
  }, [popupVisible, locationOfWaste.officialAddress]);

  return (
    <div className={"h-[70vh] xl:w-[70vw]"}>
      <ArcMapView
        style={{ height: "100%" }}
        map={{ basemap: "streets-navigation-vector" }}
        zoom={12.5}
        constraints={{
          snapToZoom: false,
          minZoom: 12,
          maxZoom: 17,
        }}
        center={
          latitude && longitude && inBoundary
            ? [Number.parseFloat(longitude), Number.parseFloat(latitude)]
            : initialCenterWCC
        }
        onViewCreated={setMapView}
      >
        <ArcUI position="bottom-right">
          <ArcZoom />
        </ArcUI>
        <ArcUI position="bottom-left">
          <ArcScaleBar unit="dual" />
        </ArcUI>
        <Layers locationUpdate={updateLocationOfWaste} />
      </ArcMapView>
    </div>
  );
};
